/**
 * Clients and bill-to addresses — the two halves of "who".
 *
 * They are separate on purpose, and the example invoice is why: work recorded
 * against RealPage, Kalshi and EDNY was billed to Taylored Consulting Services.
 * A client says what the work was for; a bill-to says who pays for it. Neither
 * is a special case of the other, and a client can be billed to whoever is
 * paying that month.
 *
 * A client's rate is a default, not a rule. Leaving it blank means "whatever
 * the application default is", so raising that one number in Settings moves
 * every client that never set its own.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type { BillTo, ConsultingClient, RateKind } from '@/api/types'
import { useToast } from '@/components/Toast'
import { EmptyState, ErrorState, Field, LoadingBlock } from '@/components/ui'

export function ConsultingClientsPage() {
  const toast = useToast()
  const [clients, setClients] = useState<ConsultingClient[]>([])
  const [billTos, setBillTos] = useState<BillTo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [nextClients, nextBillTos] = await Promise.all([
        api.consulting.clients.list(true),
        api.consulting.billTos.list(true),
      ])
      setClients(nextClients)
      setBillTos(nextBillTos)
      setError(null)
    } catch (caught) {
      if (caught instanceof ApiError && !caught.isUnauthorized) setError(caught.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const act = useCallback(
    async (action: () => Promise<unknown>, success: string) => {
      try {
        await action()
        await load()
        toast.success(success)
      } catch (caught) {
        toast.error(caught instanceof ApiError ? caught.message : 'That did not work.')
      }
    },
    [load, toast],
  )

  if (loading) return <LoadingBlock label="Loading clients" />
  if (error) return <ErrorState message={error} onRetry={() => void load()} />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-bold">Clients</h1>
        <Link to="/consulting" className="btn btn-neutral btn-sm ml-auto">
          Back to time
        </Link>
      </div>

      <section className="card overflow-hidden">
        <div className="border-b border-line px-3 py-2.5">
          <h2 className="text-sm font-semibold">Clients</h2>
          <p className="mt-0.5 text-xs text-ink-3">
            What the work is recorded against. Leave the rate blank to use the application
            default, or set a client to <strong>Joint venture</strong> so its time bills no
            money at all.
          </p>
        </div>

        {clients.length === 0 ? (
          <EmptyState title="No clients yet">
            Add the first one below. &ldquo;Misc&rdquo; is a perfectly good client — the example
            invoice bills a server cost through it as one hour.
          </EmptyState>
        ) : (
          // A table, not a list of labelled fields: every row has the same four
          // columns, so naming them once at the top beats repeating four labels
          // on every client. Its own scroller, because five columns of controls
          // do not fit on a phone and the page must never scroll sideways.
          <div className="overflow-x-auto">
            <table className="w-full min-w-3xl border-collapse text-sm">
              <thead>
                <tr className="bg-surface-3 text-left text-xs uppercase tracking-wide text-ink-3">
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Client
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Bills at
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Rate
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Billed to
                  </th>
                  {/* `relative` is load-bearing: `sr-only` is absolutely
                      positioned, and without a positioned ancestor it escapes
                      the table's horizontal scroller and stretches the whole
                      document sideways on a phone. */}
                  <th scope="col" className="relative px-3 py-2 text-right font-semibold">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <ClientRow
                    key={client.id}
                    client={client}
                    billTos={billTos}
                    onPatch={(patch) =>
                      act(() => api.consulting.clients.update(client.id, patch), 'Saved.')
                    }
                    onDelete={() =>
                      act(() => api.consulting.clients.remove(client.id), 'Deleted.')
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <NewClient
          billTos={billTos}
          onCreate={(payload) => act(() => api.consulting.clients.create(payload), 'Added.')}
        />
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-line px-3 py-2.5">
          <h2 className="text-sm font-semibold">Bill to</h2>
          <p className="mt-0.5 text-xs text-ink-3">
            Who pays. The address block prints under BILL TO:, one line per line you type, and
            the Re: line says what the engagement is.
          </p>
        </div>

        {billTos.length === 0 ? (
          <EmptyState title="Nowhere to bill yet">
            An invoice needs one of these before it can be raised.
          </EmptyState>
        ) : (
          <ul className="m-0 list-none p-0">
            {billTos.map((entry) => (
              <BillToRow
                key={entry.id}
                billTo={entry}
                onPatch={(patch) =>
                  act(() => api.consulting.billTos.update(entry.id, patch), 'Saved.')
                }
                onDelete={() => act(() => api.consulting.billTos.remove(entry.id), 'Deleted.')}
              />
            ))}
          </ul>
        )}

        <NewBillTo
          onCreate={(payload) => act(() => api.consulting.billTos.create(payload), 'Added.')}
        />
      </section>
    </div>
  )
}

function ClientRow({
  client,
  billTos,
  onPatch,
  onDelete,
}: {
  client: ConsultingClient
  billTos: BillTo[]
  onPatch: (patch: Record<string, unknown>) => Promise<void>
  onDelete: () => Promise<void>
}) {
  const [name, setName] = useState(client.name)
  const [rate, setRate] = useState(client.default_rate === null ? '' : String(client.default_rate))

  useEffect(() => setName(client.name), [client.name])
  useEffect(
    () => setRate(client.default_rate === null ? '' : String(client.default_rate)),
    [client.default_rate],
  )

  return (
    <tr className="border-t border-line">
      <td className="px-3 py-1.5">
        <input
          className={`field py-1 text-sm ${client.is_archived ? 'text-ink-3' : ''}`}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => {
            if (name.trim() && name !== client.name) void onPatch({ name: name.trim() })
          }}
          aria-label={`Name of ${client.name}`}
        />
      </td>

      <td className="px-3 py-1.5">
        <select
          className="field py-1 text-xs"
          value={client.default_rate_kind}
          onChange={(event) => void onPatch({ default_rate_kind: event.target.value as RateKind })}
          aria-label={`How ${client.name} bills`}
        >
          <option value="hourly">An hourly rate</option>
          <option value="joint_venture">Joint venture</option>
        </select>
      </td>

      <td className="px-3 py-1.5">
        {client.default_rate_kind === 'hourly' ? (
          <input
            type="number"
            min="0"
            step="0.01"
            className="field py-1 text-xs"
            value={rate}
            // The application default, shown greyed so it is clear the box is
            // empty rather than set to that number.
            placeholder={String(client.effective_rate ?? '')}
            onChange={(event) => setRate(event.target.value)}
            onBlur={() => {
              const next = rate === '' ? null : Number(rate)
              if (next !== client.default_rate) void onPatch({ default_rate: next })
            }}
            aria-label={`Rate for ${client.name}`}
          />
        ) : (
          <span className="text-xs text-ink-3">&mdash;</span>
        )}
      </td>

      <td className="px-3 py-1.5">
        <select
          className="field py-1 text-xs"
          value={client.default_bill_to_id ?? ''}
          onChange={(event) =>
            void onPatch({
              default_bill_to_id: event.target.value ? Number(event.target.value) : null,
            })
          }
          aria-label={`Who ${client.name} is billed to`}
        >
          <option value="">nobody in particular</option>
          {billTos.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </td>

      <td className="px-3 py-1.5">
        <span className="flex items-center justify-end gap-2">
          {client.is_archived && <span className="pill bg-gray-200 text-gray-700">Archived</span>}
          <button
            type="button"
            className="btn btn-neutral btn-sm"
            onClick={() => void onPatch({ is_archived: !client.is_archived })}
          >
            {client.is_archived ? 'Restore' : 'Archive'}
          </button>
          {/* Deleting is refused by the server once any time has been recorded
              against the client, which is why it sits beside Archive rather
              than behind a confirmation. */}
          <button type="button" className="btn btn-neutral btn-sm" onClick={() => void onDelete()}>
            Delete
          </button>
        </span>
      </td>
    </tr>
  )
}

function NewClient({
  billTos,
  onCreate,
}: {
  billTos: BillTo[]
  onCreate: (payload: {
    name: string
    default_rate_kind: RateKind
    default_rate: number | null
    default_bill_to_id: number | null
  }) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [kind, setKind] = useState<RateKind>('hourly')
  const [rate, setRate] = useState('')
  const [billToId, setBillToId] = useState<number | ''>('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    await onCreate({
      name: name.trim(),
      default_rate_kind: kind,
      default_rate: kind === 'hourly' && rate ? Number(rate) : null,
      default_bill_to_id: billToId === '' ? null : billToId,
    })
    setName('')
    setRate('')
  }

  return (
    // The columns above are named once in the table header; here every control
    // carries its own label, because this is the form you meet before you know
    // what any of them do.
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2 bg-surface-2 px-3 py-2">
      <Field label="New client" className="w-52">
        <input
          className="field py-1 text-sm"
          placeholder="RealPage, EDNY, Misc..."
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field label="Bills at" className="w-40">
        <select
          className="field py-1 text-xs"
          value={kind}
          onChange={(event) => setKind(event.target.value as RateKind)}
        >
          <option value="hourly">An hourly rate</option>
          <option value="joint_venture">Joint venture</option>
        </select>
      </Field>
      {kind === 'hourly' && (
        <Field label="Rate" className="w-28">
          <input
            type="number"
            min="0"
            step="0.01"
            className="field py-1 text-xs"
            placeholder="Default"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
          />
        </Field>
      )}
      <Field label="Billed to" className="w-48">
        <select
          className="field py-1 text-xs"
          value={billToId}
          onChange={(event) => setBillToId(event.target.value ? Number(event.target.value) : '')}
        >
          <option value="">nobody in particular</option>
          {billTos.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </Field>
      <button type="submit" className="btn btn-primary btn-sm" disabled={!name.trim()}>
        Add
      </button>
    </form>
  )
}

function BillToRow({
  billTo,
  onPatch,
  onDelete,
}: {
  billTo: BillTo
  onPatch: (patch: Record<string, unknown>) => Promise<void>
  onDelete: () => Promise<void>
}) {
  const [name, setName] = useState(billTo.name)
  const [address, setAddress] = useState(billTo.address ?? '')
  const [reLine, setReLine] = useState(billTo.re_line ?? '')

  useEffect(() => setName(billTo.name), [billTo.name])
  useEffect(() => setAddress(billTo.address ?? ''), [billTo.address])
  useEffect(() => setReLine(billTo.re_line ?? ''), [billTo.re_line])

  return (
    <li className="border-b border-line px-3 py-2 last:border-b-0">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Name" className="w-64">
          <input
            className="field py-1 text-sm font-semibold"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              if (name.trim() && name !== billTo.name) void onPatch({ name: name.trim() })
            }}
          />
        </Field>
        <Field label="Re:" className="min-w-48 flex-1">
          <input
            className="field py-1 text-sm"
            placeholder="What the engagement is"
            value={reLine}
            onChange={(event) => setReLine(event.target.value)}
            onBlur={() => {
              if (reLine !== (billTo.re_line ?? '')) void onPatch({ re_line: reLine })
            }}
          />
        </Field>
        <button type="button" className="btn btn-neutral btn-sm" onClick={() => void onDelete()}>
          Delete
        </button>
      </div>
      <Field label="Address" className="mt-2">
        <textarea
          className="field text-sm"
          rows={2}
          placeholder="Street&#10;City, State ZIP"
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          onBlur={() => {
            if (address !== (billTo.address ?? '')) void onPatch({ address })
          }}
        />
      </Field>
    </li>
  )
}

function NewBillTo({
  onCreate,
}: {
  onCreate: (payload: { name: string; address: string | null; re_line: string | null }) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [reLine, setReLine] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    await onCreate({
      name: name.trim(),
      address: address.trim() || null,
      re_line: reLine.trim() || null,
    })
    setName('')
    setAddress('')
    setReLine('')
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2 bg-surface-2 px-3 py-2">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="New bill-to" className="w-64">
          <input
            className="field py-1 text-sm"
            placeholder="Who pays"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Re:" className="min-w-48 flex-1">
          <input
            className="field py-1 text-sm"
            placeholder="What the engagement is"
            value={reLine}
            onChange={(event) => setReLine(event.target.value)}
          />
        </Field>
        <button type="submit" className="btn btn-primary btn-sm" disabled={!name.trim()}>
          Add
        </button>
      </div>
      <Field label="Address">
        <textarea
          className="field text-sm"
          rows={2}
          placeholder="Street&#10;City, State ZIP"
          value={address}
          onChange={(event) => setAddress(event.target.value)}
        />
      </Field>
    </form>
  )
}
